import React, { useState, useEffect } from 'react';
import { Plus, Edit, AlertCircle, Building2, Save, X } from 'lucide-react';
import { api } from '../services/api';
import { authService } from '../services/authService';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useAdminToast } from './admin/AdminToast';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const BOCManagement = () => {
  const [bocs, setBocs] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showSuccess, toastElement } = useAdminToast();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingBOC, setEditingBOC] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    bocId: '',
    name: '',
    description: '',
    enterpriseIdFk: '',
  });

  useEffect(() => {
    loadBOCs();
    loadEnterprises();
  }, []);

  const loadBOCs = async () => {
    setError('');
    try {
      const response = await api.get('/admin/bocs');
      setBocs(response.data);
    } catch (error) {
      console.error('Error loading BOCs:', error);
      setError('Failed to load BOCs');
    } finally {
      setLoading(false);
    }
  };

  const loadEnterprises = async () => {
    setError('');
    try {
      const response = await api.get('/admin/enterprises');
      setEnterprises(response.data);
    } catch (error) {
      console.error('Error loading enterprises:', error);
    }
  };

  const handleCreateBOC = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/admin/bocs', formData);
      await loadBOCs();
      setShowCreateForm(false);
      resetForm();
      showSuccess(`BOC "${formData.name || formData.bocId}" created`);
    } catch (error) {
      console.error('Error creating BOC:', error);
      setError(error.response?.data?.message || 'Failed to create BOC');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateBOC = async (e) => {
    e.preventDefault();
    if (!editingBOC) return;
    setSubmitting(true);

    setError('');
    try {
      await api.put(`/admin/bocs/${editingBOC.id}`, formData);
      await loadBOCs();
      setEditingBOC(null);
      setShowCreateForm(false);
      resetForm();
      showSuccess(`BOC "${formData.name || formData.bocId}" updated`);
    } catch (error) {
      console.error('Error updating BOC:', error);
      setError(error.response?.data?.message || 'Failed to update BOC');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisableBOC = async (id) => {
    const boc = bocs.find((b) => b.id === id);
    const label = boc ? (boc.name || boc.bocId) : 'this BOC';
    const confirmed = await confirm({
      title: `Deactivate BOC "${label}"?`,
      message: `"${label}" will no longer be selectable elsewhere in the app.`,
      confirmLabel: 'Deactivate',
    });
    if (!confirmed) return;

    setError('');
    try {
      await api.delete(`/admin/bocs/${id}`);
      await loadBOCs();
      showSuccess(`BOC "${label}" disabled`);
    } catch (error) {
      console.error('Error disabling BOC:', error);
      setError(error.response?.data?.message || 'Failed to disable BOC');
    }
  };

  const startEdit = (boc) => {
    setEditingBOC(boc);
    setError('');
    setFormData({
      bocId: boc.bocId || '',
      name: boc.name || '',
      description: boc.description || '',
      enterpriseIdFk: boc.enterpriseIdFk || '',
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      bocId: '',
      name: '',
      description: '',
      enterpriseIdFk: '',
    });
  };

  if (loading) return <AdminLoading message="Loading BOCs..." />;

  return (
    <div className="space-y-6">
      {toastElement}
      {confirmDialog}
      <AdminPageHeader
        icon={Building2}
        title="BOC Management"
        subtitle="Manage internal BOCs mapped to enterprises"
        rightContent={
          !showCreateForm && (
            <AdminAddButton
              onClick={() => {
                setShowCreateForm(true);
                setEditingBOC(null);
                setError('');
                resetForm();
              }}
              label="Add BOC"
            />
          )
        }
      />

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              {editingBOC ? (
                <>
                  <Edit className="h-6 w-6 mr-2 text-blue-600" />
                  Edit BOC
                </>
              ) : (
                <>
                  <Plus className="h-6 w-6 mr-2 text-blue-600" />
                  Create New BOC
                </>
              )}
            </h3>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setEditingBOC(null);
                setError('');
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={editingBOC ? handleUpdateBOC : handleCreateBOC} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter BOC name"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">BOC ID</label>
              <input
                type="text"
                value={formData.bocId}
                onChange={(e) => setFormData({ ...formData, bocId: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter BOC ID"
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Enterprise</label>
              <select
                value={formData.enterpriseIdFk}
                onChange={(e) => setFormData({ ...formData, enterpriseIdFk: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={submitting}
              >
                <option value="">Select Enterprise</option>
                {enterprises.map((ent) => (
                  <option key={ent.id} value={ent.id}>
                    {ent.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter description"
                rows="3"
                disabled={submitting}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingBOC(null);
                  setError('');
                  resetForm();
                }}
                className="px-5 py-2.5 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                <X className="h-4 w-4 inline mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingBOC ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 inline mr-2" />
                    {editingBOC ? 'Update BOC' : 'Create BOC'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* BOCs Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-gray-600" />
            BOCs ({bocs.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">BOC</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Enterprise</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bocs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No BOCs found. Create one to get started.</p>
                  </td>
                </tr>
              ) : (
                bocs.map((boc) => (
                  <tr key={boc.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{boc.name || boc.bocId || '-'}</div>
                      {boc.name && <div className="text-xs text-gray-400">{boc.bocId}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {enterprises.find(e => e.id === boc.enterpriseIdFk)?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{boc.description || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        boc.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        <span className={`w-2 h-2 rounded-full mr-2 ${
                          boc.active ? 'bg-green-500' : 'bg-red-500'
                        }`}></span>
                        {boc.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <AdminRowActions align="start">
                        <AdminActionButton variant="edit" onClick={() => startEdit(boc)} />
                        <AdminActionButton variant="deactivate" onClick={() => handleDisableBOC(boc.id)} />
                      </AdminRowActions>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BOCManagement;
